import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Upload, StickyNote, Plus, Edit, Trash2, Save, X, Loader2, CheckCircle, Eye, AlertTriangle, Mail, Bell, Play, Settings as SettingsIcon, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import PermissionsDialog from '../components/PermissionsDialog';
import { useUnreadNotes } from '../contexts/UnreadNotesContext';
import { compressImage } from '../utils/imageCompression';
import { queueRequest, isNetworkError } from '../utils/offlineQueue';
import { usePermissions } from '../hooks/usePermissions';
import PhotoGallery from '../components/PhotoGallery';
import ProjectTasks from '../components/ProjectTasks';
import ProjectSubContractors from '../components/ProjectSubContractors';
import { SubForYou } from '../components/SubForYou';
import { SubPhotoGallery } from '../components/SubPhotoGallery';
import { ProjectMockups } from '../components/ProjectMockups';
import { ProjectFiles } from '../components/ProjectFiles';
import Slideshow from '../components/Slideshow';
import SlideshowSettingsDialog from '../components/SlideshowSettingsDialog';
import PhotoUploadPermissionsDialog from '../components/PhotoUploadPermissionsDialog';
import { DeficienciesTab } from '../components/DeficienciesTab';
import ProjectToolsSection from '../components/ProjectToolsSection';
import { getBackendUrl } from '../utils/getBackendUrl';
import NoteReminderDialog from '../components/NoteReminderDialog';

const ProjectDetail = ({ user }) => {
  const { can, loading: permissionsLoading } = usePermissions();
  const { projectId } = useParams();
  const id = projectId; // Use projectId from route params
  const navigate = useNavigate();
  const { triggerRefresh } = useUnreadNotes();
  const [project, setProject] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  // Manual notification state
  const [photoNotificationStatus, setPhotoNotificationStatus] = useState(null);
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [reminderNoteId, setReminderNoteId] = useState(null);
  // Slideshow state
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowSettingsOpen, setSlideshowSettingsOpen] = useState(false);
  const [slideshowSettings, setSlideshowSettings] = useState({ enabled: true, duration_seconds: 5, transition: 'fade', show_captions: true, sort_order: 'asc' });
  // Per-project photo upload permission (computed by backend including overrides)
  const [canUploadPhotos, setCanUploadPhotos] = useState(false);
  const [uploadPermissionReady, setUploadPermissionReady] = useState(false);
  const [photoPermissionsOpen, setPhotoPermissionsOpen] = useState(false);
  const [projectMapLocation, setProjectMapLocation] = useState(null);

  // Helper to get API base URL dynamically
  const getApiUrl = () => `${getBackendUrl()}/api`;

  useEffect(() => {
    if (user.role === 'client') return;
    axios.get(`${getApiUrl()}/job-locations`)
      .then(res => {
        const match = (res.data || []).find(l => l.project_id === id);
        setProjectMapLocation(match || null);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [id, user.role]);

  useEffect(() => {
    let isMounted = true;
    
    // Force refresh photos when page becomes visible (critical for mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user.role !== 'sub_contractor') {
        console.log('[ProjectDetail] Page visible - refreshing photos');
        fetchPhotos();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const loadData = async () => {
      if (isMounted) {
        await fetchProjectDetails();
        if (user.role !== 'sub_contractor') {
          await fetchPhotos();
          await fetchNotes();
          await markNotesAsViewed();
        }
        await fetchSlideshowSettings();
        await fetchPhotoUploadPermission();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id]);

  const markPhotosAsViewed = async () => {
    try {
      await axios.post(`${getApiUrl()}/photos/client/mark-viewed?project_id=${id}`);
      console.log('[ProjectDetail] Client viewed photos - admin notified');
    } catch (error) {
      console.error('[ProjectDetail] Failed to mark photos as viewed:', error);
    }
  };

  const fetchSlideshowSettings = async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/projects/${id}/slideshow-settings`);
      setSlideshowSettings(res.data || slideshowSettings);
    } catch (error) {
      // Non-fatal — keep defaults
      console.error('Failed to fetch slideshow settings');
    }
  };

  const markNotesAsViewed = async () => {
    try {
      await axios.post(`${getApiUrl()}/notes/mark-viewed/${id}`);
      // Debounced refresh to prevent photo flickering
      setTimeout(() => {
        triggerRefresh();
      }, 1000);
    } catch (error) {
      console.error('Failed to mark notes as viewed');
    }
  };

  const fetchProjectDetails = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project details');
      navigate('/projects');
    }
  };

  // Ask backend whether this user can upload photos to this project
  // (admin/global permission OR project-level override).
  const fetchPhotoUploadPermission = async () => {
    if (user?.role === 'client') {
      setCanUploadPhotos(false);
      setUploadPermissionReady(true);
      return;
    }
    try {
      const res = await axios.get(`${getApiUrl()}/projects/${id}/photo-upload-permission`);
      setCanUploadPhotos(!!res.data?.can_upload);
    } catch {
      setCanUploadPhotos(false);
    } finally {
      setUploadPermissionReady(true);
    }
  };

  const fetchPhotos = async (skipUpdate = false) => {
    try {
      console.log('[ProjectDetail] Fetching photos for project:', id);
      // Add cache-busting timestamp parameter (critical for mobile Safari)
      const timestamp = new Date().getTime();
      const response = await axios.get(`${getApiUrl()}/photos/${id}?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('[ProjectDetail] Photos response:', {
        count: response.data?.length,
        isArray: Array.isArray(response.data),
        photos: response.data?.map(p => ({
          id: p.id,
          file_path: p.file_path,
          description: p.description
        }))
      });
      
      if (!skipUpdate && Array.isArray(response.data)) {
        // Only update if we got valid data - never clear existing photos on error
        setPhotos(response.data);
        console.log('[ProjectDetail] Photos state updated with', response.data.length, 'photos');
        
        // If client is viewing photos, mark as viewed and notify admin
        if (user.role === 'client' && response.data.length > 0) {
          markPhotosAsViewed();
        }
      }
    } catch (error) {
      console.error('[ProjectDetail] Failed to fetch photos:', error);
      // Don't clear existing photos on fetch error - keep showing what we have
      console.log('[ProjectDetail] Keeping existing photos despite error');
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/notes/${id}`);
      if (Array.isArray(response.data)) {
        setNotes(response.data);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Failed to fetch notes');
      setNotes([]);
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    if (!uploadFiles || uploadFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    // Validate all files
    for (let file of uploadFiles) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image file`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB`);
        return;
      }
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: uploadFiles.length });
    
    let successCount = 0;
    let failCount = 0;
    let queuedCount = 0;

    try {
      // Upload each file sequentially
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        setUploadProgress({ current: i + 1, total: uploadFiles.length });
        
        try {
          toast.info(`Processing ${file.name}... (${i + 1}/${uploadFiles.length})`);
          
          // Compress image before upload
          const compressedFile = await compressImage(file, 1280, 1280, 0.8);
          
          if (!compressedFile || compressedFile.size === 0) {
            throw new Error('Image compression failed');
          }

          // Upload
          const formData = new FormData();
          formData.append('file', compressedFile);
          formData.append('project_id', id);
          if (uploadDescription) {
            formData.append('description', uploadDescription);
          }

          await axios.post(`${getApiUrl()}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
          });
          
          successCount++;
        } catch (error) {
          if (isNetworkError(error)) {
            // Offline — queue the compressed photo for auto-sync later
            try {
              const compressedForQueue = await compressImage(file, 1280, 1280, 0.8);
              const formFields = { project_id: id };
              if (uploadDescription) formFields.description = uploadDescription;
              await queueRequest({
                url: `${getApiUrl()}/photos`,
                method: 'POST',
                fileBlob: compressedForQueue,
                fileName: compressedForQueue.name,
                formFields,
                description: 'Photo upload',
              });
              queuedCount++;
              continue;
            } catch { /* fall through to failure */ }
          }
          console.error(`Failed to upload ${file.name}:`, error);
          failCount++;
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      
      // Show summary
      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} photo${successCount > 1 ? 's' : ''}!`);
      }
      if (queuedCount > 0) {
        toast.success(`No signal — ${queuedCount} photo${queuedCount > 1 ? 's' : ''} saved on this device and will upload automatically`);
      }
      if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} photo${failCount > 1 ? 's' : ''}`);
      }
      
      // Close dialog and refresh if at least one succeeded or was queued
      if (successCount > 0 || queuedCount > 0) {
        setIsUploadOpen(false);
        setUploadFiles([]);
        setUploadDescription('');
        fetchPhotos();
      }
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNoteContent.trim()) return;

    try {
      await axios.post(`${getApiUrl()}/notes`, {
        project_id: id,
        content: newNoteContent
      });
      toast.success('Note added successfully!');
      setNewNoteContent('');
      setIsNoteDialogOpen(false);
      fetchNotes();
    } catch (error) {
      if (isNetworkError(error)) {
        await queueRequest({
          url: `${getApiUrl()}/notes`,
          method: 'POST',
          jsonBody: { project_id: id, content: newNoteContent },
          description: 'Project note',
        });
        toast.success('No signal — note saved on this device and will sync automatically');
        setNewNoteContent('');
        setIsNoteDialogOpen(false);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to create note');
    }
  };

  const handleUpdateNote = async (noteId, content) => {
    try {
      await axios.put(`${getApiUrl()}/notes/${noteId}`, { content });
      toast.success('Note updated successfully!');
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await axios.delete(`${getApiUrl()}/notes/${noteId}`);
      toast.success('Note deleted successfully!');
      fetchNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    }
  };

  const canEditNote = (note) => {
    return (user.role === 'admin' || user.role === 'supervisor') || note.user_id === user.id;
  };

  const handleMarkNoteAsRead = async (noteId) => {
    try {
      await axios.post(`${getApiUrl()}/notes/${noteId}/mark-read`);
      toast.success('Marked as read');
      fetchNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as read');
    }
  };

  const canSeeReadReceipts = (note) => {
    // Note author and admins can see who read it
    return (user.role === 'admin' || user.role === 'supervisor') || note.user_id === user.id;
  };

  const hasUserReadNote = (note) => {
    return note.read_receipts?.some(r => r.user_id === user.id) || false;
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo? This action cannot be undone.')) return;

    try {
      await axios.delete(`${getApiUrl()}/photos/${photoId}`);
      toast.success('Photo deleted successfully');
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const handleSetHeroPhoto = async (photo) => {
    try {
      await axios.put(`${getApiUrl()}/photos/${photo.id}/set-hero`);
      toast.success('Set as hero photo for client dashboard!');
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set hero photo');
    }
  };

  const fetchPhotoNotificationStatus = async () => {
    if (user.role !== 'admin') return;
    
    try {
      const response = await axios.get(`${getApiUrl()}/photos/${id}/notification-status`);
      setPhotoNotificationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch photo notification status:', error);
    }
  };

  const sendManualPhotoNotification = async () => {
    setIsSendingNotification(true);
    try {
      const response = await axios.post(`${getApiUrl()}/photos/send-project-update`, {
        project_id: id,
        custom_message: customMessage.trim() || undefined
      });
      
      toast.success(response.data.message);
      
      // Refresh notification status
      await fetchPhotoNotificationStatus();
      
      // Close dialog and reset state
      setIsNotificationDialogOpen(false);
      setCustomMessage('');
      
    } catch (error) {
      console.error('Failed to send photo notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send photo notification');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const openNotificationDialog = () => {
    setCustomMessage('');
    setIsNotificationDialogOpen(true);
    // Fetch current status when dialog opens
    fetchPhotoNotificationStatus();
  };

  const canEditDeletePhoto = () => {
    return (user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee';
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  const canAddNotes = ['admin', 'employee'].includes(user.role);

  return (
    <div className="page-container" data-testid="project-detail-page">
      <Button
        onClick={() => navigate('/projects')}
        className="mb-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold min-h-[48px] shadow-md"
        data-testid="back-to-projects-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Projects
      </Button>

      <div className="glass-card p-4 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{project.name}</h1>
            <p className="text-sm sm:text-base text-slate-400">{project.description}</p>
          </div>
          <div className="flex gap-2 sm:gap-3 items-start flex-wrap">
            {user.role !== 'client' && projectMapLocation && (
              <Button
                onClick={() => navigate(`/job-map?focus=${projectMapLocation.id}`)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]"
                data-testid="project-map-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Map
              </Button>
            )}
            {(user.role === 'admin' || user.role === 'supervisor') && (
              <PermissionsDialog projectId={id} onUpdate={fetchProjectDetails} />
            )}
            <span className={`status-badge status-${project.status.toLowerCase()}`}>
              {project.status}
            </span>
          </div>
        </div>
        {project.timeline && (
          <div className="mb-4">
            <span className="text-slate-400">Timeline: </span>
            <span className="text-white font-semibold">{project.timeline}</span>
          </div>
        )}
      </div>

      {user.role !== 'client' && (
        <div className="glass-card p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <div className="p-2 bg-yellow-500/20 rounded-lg flex-shrink-0">
                <StickyNote className="w-5 h-5 text-yellow-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Project Notes</h2>
            {notes.length > 0 && (
              <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-orange-500 rounded-full flex-shrink-0">
                {notes.length}
              </span>
            )}
          </div>
          {canAddNotes && (
            <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-yellow-500 to-yellow-600 min-h-[44px] text-sm sm:text-base w-full sm:w-auto" data-testid="add-note-btn">
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Add Note</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 text-white border-slate-700">
                <DialogHeader>
                  <DialogTitle>Add New Note</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateNote} className="space-y-4" data-testid="create-note-form">
                  <div>
                    <Label htmlFor="note-content">Note Content</Label>
                    <Textarea
                      id="note-content"
                      data-testid="note-content-input"
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      placeholder="Enter your note here..."
                      required
                      className="mt-1 min-h-[120px]"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600" data-testid="submit-note-btn">
                    Add Note
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {notes.length === 0 ? (
          <div className="text-center py-8">
            <StickyNote className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No notes yet. {canAddNotes && 'Add the first note!'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700" data-testid={`note-${note.id}`}>
                {editingNote === note.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      className="min-h-[100px]"
                      data-testid={`edit-note-textarea-${note.id}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateNote(note.id, newNoteContent)}
                        className="bg-gradient-to-r from-green-500 to-green-600"
                        data-testid={`save-note-${note.id}`}
                      >
                        <Save className="w-3 h-3 mr-1" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingNote(null);
                          setNewNoteContent('');
                        }}
                        data-testid={`cancel-edit-${note.id}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold">{note.user_name}</span>
                        <span className="text-slate-500 text-sm">•</span>
                        <span className="text-slate-400 text-sm">
                          {new Date(note.updated_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                        </span>
                        {note.created_at !== note.updated_at && (
                          <span className="text-slate-500 text-xs">(edited)</span>
                        )}
                      </div>
                      {canEditNote(note) && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setReminderNoteId(note.id)}
                            className="text-orange-400 hover:text-orange-300"
                            data-testid={`reminder-note-btn-${note.id}`}
                            title="Set Reminder"
                          >
                            <Bell className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingNote(note.id);
                              setNewNoteContent(note.content);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            data-testid={`edit-note-btn-${note.id}`}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-note-btn-${note.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-white text-base whitespace-pre-wrap mb-3">{note.content}</p>
                    
                    {/* Read Receipts and Mark as Read Button */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                      <div className="flex-1">
                        {canSeeReadReceipts(note) && note.read_receipts && note.read_receipts.length > 0 && (
                          <div className="text-xs text-slate-400">
                            <div className="flex items-center gap-1 mb-1">
                              <Eye className="w-3 h-3" />
                              <span className="font-semibold">Read by:</span>
                            </div>
                            <div className="space-y-1 ml-4">
                              {note.read_receipts.map((receipt) => (
                                <div key={receipt.user_id}>
                                  {receipt.user_name} <span className="text-slate-500">({formatRelativeTime(receipt.read_at)})</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {!hasUserReadNote(note) && (
                        <Button
                          size="sm"
                          onClick={() => handleMarkNoteAsRead(note.id)}
                          className="bg-green-600 hover:bg-green-700 text-white ml-2 flex-shrink-0"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark as Read
                        </Button>
                      )}
                      {hasUserReadNote(note) && (
                        <div className="flex items-center gap-1 text-green-500 text-xs ml-2 flex-shrink-0">
                          <CheckCircle className="w-3 h-3" />
                          <span>You read this</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      )}

      {/* Project Files — PDFs for the crew (hidden from clients) */}
      {user.role !== 'client' && <ProjectFiles projectId={id} user={user} />}

      {/* Deficiencies Section - Admin and Employees Only */}
      {((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee') && (
        <div className="glass-card p-4 sm:p-6 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-orange-500" />
            Deficiencies & Issues
          </h2>
          <DeficienciesTab projectId={id} userRole={user.role} />
        </div>
      )}

      {/* For You — targeted photos & notes + photo gallery for the logged-in sub-contractor */}
      {user.role === 'sub_contractor' && (
        <div className="mb-6 space-y-6">
          <SubPhotoGallery projectId={id} subId={user.id} user={user} />
          <SubForYou projectId={id} subId={user.id} notesOnly />
        </div>
      )}

      {/* Sub-Contractors — admin adds subs + targeted photos/notes (hidden from clients) */}
      {((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee') && (
        <ProjectSubContractors projectId={id} user={user} />
      )}

      {/* Project Tools Section - Admin and Employees Only */}
      {((user.role === 'admin' || user.role === 'supervisor') || user.role === 'employee') && project && (
        <ProjectToolsSection projectId={id} projectName={project.name} user={user} />
      )}

      {/* Projects To Complete — admin creates tasks, employees check them off, clients can view */}
      <ProjectTasks projectId={id} user={user} />

      {/* Project Photo Mockup — admin uploads mockups for the client */}
      {user.role !== 'sub_contractor' && <ProjectMockups projectId={id} user={user} />}

      <div className="glass-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Project Gallery</h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
            {/* Play Slideshow button - visible if photos exist AND (admin OR slideshow enabled) */}
            {photos.length > 0 && ((user.role === 'admin' || user.role === 'supervisor') || slideshowSettings.enabled) && (
              <Button
                onClick={() => setSlideshowOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white min-h-[44px] text-sm sm:text-base w-full sm:w-auto shadow-md"
                data-testid="play-slideshow-btn"
              >
                <Play className="w-4 h-4 mr-1 sm:mr-2 fill-white" />
                <span className="whitespace-nowrap">Play Slideshow</span>
              </Button>
            )}
            {/* Slideshow settings - admin only */}
            {(user.role === 'admin' || user.role === 'supervisor') && (
              <Button
                onClick={() => setSlideshowSettingsOpen(true)}
                variant="outline"
                className="min-h-[44px] text-sm sm:text-base w-full sm:w-auto bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white"
                data-testid="slideshow-settings-btn"
                title="Slideshow Settings"
              >
                <SettingsIcon className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Slideshow Settings</span>
              </Button>
            )}
            {/* Photo upload permissions - admin only */}
            {(user.role === 'admin' || user.role === 'supervisor') && (
              <Button
                onClick={() => setPhotoPermissionsOpen(true)}
                variant="outline"
                className="min-h-[44px] text-sm sm:text-base w-full sm:w-auto bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white"
                data-testid="photo-permissions-btn"
                title="Who can upload photos to this project"
              >
                <Upload className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Photo Uploaders</span>
              </Button>
            )}
            {/* Manual Photo Notification Button - Admin only */}
            {(user.role === 'admin' || user.role === 'supervisor') && project && project.assigned_clients && project.assigned_clients.length > 0 && (
              <Button
                onClick={openNotificationDialog}
                className="bg-gradient-to-r from-green-500 to-green-600 min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
                data-testid="notify-client-btn"
              >
                <Mail className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Notify Client</span>
              </Button>
            )}
            {/* Upload Photo Button: visible to admin/employees/subs; disabled with tooltip if not permitted */}
            {user.role !== 'client' && uploadPermissionReady && (
              canUploadPhotos ? (
                <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-orange-500 to-orange-600 min-h-[44px] text-sm sm:text-base w-full sm:w-auto" data-testid="upload-photo-btn">
                      <Upload className="w-4 h-4 mr-1 sm:mr-2 upload-icon-idle" />
                      <span className="whitespace-nowrap">Upload Photo</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 text-white border-slate-700">
              <DialogHeader>
                <DialogTitle>Upload Project Photo</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUploadPhoto} className="space-y-4" data-testid="upload-photo-form">
                <div>
                  <Label htmlFor="photo-file">Photos (select multiple)</Label>
                  <Input
                    id="photo-file"
                    data-testid="photo-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                    required
                    className="mt-1"
                  />
                  {uploadFiles.length > 0 && (
                    <p className="text-sm text-slate-400 mt-2">
                      {uploadFiles.length} photo{uploadFiles.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="photo-description">Description (Optional)</Label>
                  <Textarea
                    id="photo-description"
                    data-testid="photo-description-input"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {isUploading && uploadProgress.total > 0 && (
                  <div className="p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
                    <p className="text-sm text-blue-200">
                      Uploading {uploadProgress.current} of {uploadProgress.total}...
                    </p>
                    <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                <Button 
                  type="submit" 
                  disabled={isUploading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600" 
                  data-testid="submit-photo-btn"
                >
                  {isUploading ? (
                    <>
                      <Upload className="w-4 h-4 mr-2 upload-icon-uploading" />
                      Uploading {uploadFiles.length > 1 ? `${uploadFiles.length} Photos` : 'Photo'}...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2 upload-icon-idle" />
                      Upload Photo
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
              ) : (
                <Button
                  type="button"
                  disabled
                  title="You don't have permission to upload photos to this project. Please ask an admin."
                  className="bg-gray-200 text-gray-500 cursor-not-allowed min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
                  data-testid="upload-photo-btn-disabled"
                >
                  <Upload className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="whitespace-nowrap">Upload Photo</span>
                </Button>
              )
            )}
          </div>
        </div>

        {/* Manual Photo Notification Dialog */}
        {(user.role === 'admin' || user.role === 'supervisor') && (
          <Dialog open={isNotificationDialogOpen} onOpenChange={setIsNotificationDialogOpen}>
            <DialogContent className="bg-white text-gray-900 border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-900 font-bold">Send Photo Update Notification</DialogTitle>
              </DialogHeader>
              
              {photoNotificationStatus && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-blue-900 mb-2">{photoNotificationStatus.project_name}</h4>
                    <div className="text-sm text-blue-800 font-semibold space-y-1">
                      <p>• Total photos: {photoNotificationStatus.total_photos}</p>
                      <p>• New photos to notify: {photoNotificationStatus.new_photos_count}</p>
                      <p>• Assigned clients: {photoNotificationStatus.assigned_clients}</p>
                    </div>
                  </div>

                  {photoNotificationStatus.new_photos_count > 0 ? (
                    <>
                      <div>
                        <Label htmlFor="custom-message" className="text-gray-900 font-bold">Custom Message (Optional)</Label>
                        <Textarea
                          id="custom-message"
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          placeholder="Add a personal message about this update..."
                          className="mt-1 bg-white border-2 border-gray-400 text-gray-900 font-semibold placeholder:text-gray-400"
                          rows={4}
                        />
                        <p className="text-sm text-gray-600 mt-1">
                          Leave empty for a standard notification message.
                        </p>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setIsNotificationDialogOpen(false)}
                          disabled={isSendingNotification}
                          className="border-gray-400 text-gray-800 hover:bg-gray-100"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={sendManualPhotoNotification}
                          disabled={isSendingNotification}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {isSendingNotification ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Notification
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <Mail className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-700 font-semibold">No new photos to notify about.</p>
                      <p className="text-sm text-gray-500 mt-1">
                        All photos have already been sent in previous notifications.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setIsNotificationDialogOpen(false)}
                        className="mt-4 border-gray-400 text-gray-800 hover:bg-gray-100"
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        <PhotoGallery 
          photos={photos}
          onDelete={handleDeletePhoto}
          canDelete={canEditDeletePhoto()}
          onSetHero={handleSetHeroPhoto}
          isAdmin={(user.role === 'admin' || user.role === 'supervisor')}
          currentUserId={user.id}
          user={user}
        />
      </div>

      {/* Note Reminder Dialog */}
      <NoteReminderDialog
        noteId={reminderNoteId}
        projectId={id}
        open={!!reminderNoteId}
        onClose={() => setReminderNoteId(null)}
      />

      {/* Slideshow Viewer */}
      <Slideshow
        photos={photos}
        settings={slideshowSettings}
        open={slideshowOpen}
        onClose={() => setSlideshowOpen(false)}
      />

      {/* Slideshow Settings (admin) */}
      {(user.role === 'admin' || user.role === 'supervisor') && (
        <SlideshowSettingsDialog
          projectId={id}
          open={slideshowSettingsOpen}
          onOpenChange={setSlideshowSettingsOpen}
          onSaved={(updated) => setSlideshowSettings(updated)}
        />
      )}
      {/* Photo Upload Permissions (admin) */}
      {(user.role === 'admin' || user.role === 'supervisor') && (
        <PhotoUploadPermissionsDialog
          projectId={id}
          projectName={project?.name}
          open={photoPermissionsOpen}
          onOpenChange={setPhotoPermissionsOpen}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
